<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import type { WorkspaceDTO } from '@/types/workspace'

import WorkspaceHeadingFilter from '@/components/workspaces/WorkspaceHeadingFilter.vue'
import { appConfirm } from '@/composables/useAppConfirm'
import { useDelayedLoading } from '@/composables/useDelayedLoading'
import { getWorkspaceSyncJobs } from '@/services/testResultService'
import { getWorkspaceById } from '@/services/workspaceService'

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { userInfo, hasRole } = useUser()
const { adminModeEnabled } = useAdminMode()

// Get unsaved changes status from child components
const { childHasUnsavedChanges } = useUnsavedChangesCheck()

const workspace = ref<WorkspaceDTO | null>(null)
const loading = ref(false)
const showLoading = useDelayedLoading(loading)
const error = ref<string | null>(null)

const currentUserWorkspaceRole = computed(() => {
  const currentUserId = userInfo.value?.id

  if (!workspace.value || !currentUserId) {
    return null
  }

  const currentUserMembership = workspace.value.users.find((u) => u.userId === currentUserId)
  return currentUserMembership?.role?.trim().toLowerCase() ?? null
})

// Test results come from language test sync jobs assigned to the workspace. Admin mode alone
// should not surface the tab in workspaces that have none.
const hasSyncJobs = ref(false)

async function loadSyncJobPresence(id: number) {
  try {
    hasSyncJobs.value = (await getWorkspaceSyncJobs(id)).length > 0
  } catch {
    hasSyncJobs.value = false
  }
}

// Check if the current user is a TestResultAdmin for this workspace
const isTestResultAdmin = computed(() => {
  if (!workspace.value || !userInfo.value) return false

  // System admins have access where there are test results to manage
  if (hasRole.value('admin') && adminModeEnabled.value && hasSyncJobs.value) return true

  // Check if user is a TestResultAdmin in this workspace
  const currentUserMembership = workspace.value.users.find((u) => u.userId === userInfo.value?.id)
  return currentUserMembership?.isTestResultAdmin ?? false
})

const canManageFeeOverview = computed(() => {
  if (!workspace.value?.enableFeeManagement) {
    return false
  }

  if (hasRole.value('admin') && adminModeEnabled.value) {
    return true
  }

  switch (workspace.value.feeManagementRole?.trim().toLowerCase() ?? 'admin') {
    case 'editor':
      return ['admin', 'manager', 'editor'].includes(currentUserWorkspaceRole.value ?? '')
    case 'managers':
      return ['admin', 'manager'].includes(currentUserWorkspaceRole.value ?? '')
    case 'admin':
    default:
      return currentUserWorkspaceRole.value === 'admin'
  }
})

// Statistics cover every registration in the workspace, so they are for managers and admins.
const canViewStatistics = computed(() => canManageInvites.value)

const canManageInvites = computed(() => {
  if (!workspace.value) {
    return false
  }

  if (hasRole.value('admin') && adminModeEnabled.value) {
    return true
  }

  return ['admin', 'manager'].includes(currentUserWorkspaceRole.value ?? '')
})

// Pages whose data is scoped by the workspace-level heading filter
const headingFilterRoutes = [
  '/workspaces/[id]/overview',
  '/workspaces/[id]/events',
  '/workspaces/[id]/feeoverview',
]
const showHeadingFilter = computed(() => headingFilterRoutes.includes(String(route.name ?? '')))

const items = computed(() => {
  if (!workspace.value) {
    return []
  }

  return [
    {
      route: { name: '/workspaces/[id]/overview', params: { id: workspace.value.id } },
      label: t('router.pages.workspaces-id-overview'),
      icon: 'i-lucide-chart-bar',
    },
    {
      route: { name: '/workspaces/[id]/events', params: { id: workspace.value.id } },
      label: t('router.pages.workspaces-id-events'),
      icon: 'i-lucide-book',
    },

    {
      route: { name: '/workspaces/[id]/groups', params: { id: workspace.value.id } },
      label: t('router.pages.workspaces-id-groups'),
      icon: 'i-lucide-users',
    },
    {
      route: { name: '/workspaces/[id]/rules', params: { id: workspace.value.id } },
      label: t('router.pages.workspaces-id-rules'),
      icon: 'i-lucide-shield',
    },
    ...(canManageFeeOverview.value
      ? [
          {
            route: { name: '/workspaces/[id]/feeoverview', params: { id: workspace.value.id } },
            label: t('router.pages.workspaces-id-feeoverview'),
            icon: 'i-lucide-wallet',
          },
        ]
      : []),
    ...(canViewStatistics.value
      ? [
          {
            route: { name: '/workspaces/[id]/statistics', params: { id: workspace.value.id } },
            label: t('router.pages.workspaces-id-statistics'),
            icon: 'i-lucide-chart-line',
          },
        ]
      : []),
    ...(isTestResultAdmin.value
      ? [
          {
            route: { name: '/workspaces/[id]/testresults', params: { id: workspace.value.id } },
            label: t('router.pages.workspaces-id-testresults'),
            icon: 'i-lucide-file-check',
          },
        ]
      : []),
    ...(canManageInvites.value
      ? [
          {
            route: { name: '/workspaces/[id]/invites', params: { id: workspace.value.id } },
            label: t('router.pages.workspaces-id-invites'),
            icon: 'i-lucide-send',
          },
        ]
      : []),
    {
      route: { name: '/workspaces/[id]/settings', params: { id: workspace.value.id } },
      label: t('router.pages.workspaces-id-settings'),
      icon: 'i-lucide-settings',
    },
  ]
})

const activeTab = computed(
  () =>
    items.value.find((item) => String(route.name ?? '').includes(item.route.name))?.route.name ||
    '/workspaces/[id]/overview',
)

// Handle tab click with unsaved changes check
const handleTabClick = async (tabRoute: any) => {
  // If there are unsaved changes, show confirmation dialog
  if (childHasUnsavedChanges.value) {
    const accepted = await appConfirm({
      message: t('pages.workspaces.detail.unsavedChangesMessage'),
      title: t('pages.workspaces.detail.unsavedChangesHeader'),
      confirmColor: 'error',
    })
    if (accepted) await router.push(tabRoute)
  } else {
    // No unsaved changes, navigate directly
    await router.push(tabRoute)
  }
}

async function fetchWorkspace(id: number, keepStaleData = false) {
  loading.value = true
  error.value = null
  // Only clear workspace if not keeping stale data (initial load or different workspace)
  if (!keepStaleData) {
    workspace.value = null
    hasSyncJobs.value = false
  }
  void loadSyncJobPresence(id)
  try {
    workspace.value = await getWorkspaceById(id)
  } catch (err) {
    console.error(`Error fetching workspace(${id}):`, err)
    error.value = t('pages.workspaces.detail.loadError')
    // Clear workspace on error so error state is shown
    workspace.value = null
  } finally {
    loading.value = false
  }
}

function redirectUnauthorizedWorkspacePage() {
  if (!workspace.value) {
    return
  }

  if (route.name === '/workspaces/[id]/feeoverview' && !canManageFeeOverview.value) {
    router.replace({
      name: '/workspaces/[id]/overview',
      params: { id: workspace.value.id },
    })
    return
  }

  if (route.name === '/workspaces/[id]/statistics' && !canViewStatistics.value) {
    router.replace({
      name: '/workspaces/[id]/overview',
      params: { id: workspace.value.id },
    })
    return
  }

  if (route.name === '/workspaces/[id]/invites' && !canManageInvites.value) {
    router.replace({
      name: '/workspaces/[id]/overview',
      params: { id: workspace.value.id },
    })
  }
}

onMounted(() => {
  const id = (route.params as { id?: number }).id
  if (id) {
    fetchWorkspace(id)
  }
})

watch(
  () => (route.params as { id?: number }).id,
  (newIdParam) => {
    if (newIdParam) {
      fetchWorkspace(newIdParam)
    }
  },
)

const { setBreadcrumbs, buildWorkspaceBreadcrumbs } = useBreadcrumbs()

// Update breadcrumbs when workspace data changes
watch(workspace, (newOrg) => {
  redirectUnauthorizedWorkspacePage()

  if (newOrg) {
    const currentPageTitle =
      route.name === '/workspaces/[id]/overview'
        ? undefined
        : `router.pages.${String(route.name)
            .split('/')
            .filter((x) => x)
            .join('-')
            .replace(/\[/g, '')
            .replace(/\]/g, '')}`

    const breadcrumbItems = buildWorkspaceBreadcrumbs(newOrg.id, newOrg.nameDE, currentPageTitle)

    setBreadcrumbs(breadcrumbItems)
  }
})

// Also update breadcrumbs when route changes (for sub-pages)
watch(
  () => route.name,
  () => {
    redirectUnauthorizedWorkspacePage()

    if (workspace.value) {
      const currentPageTitle =
        route.name === '/workspaces/[id]/overview'
          ? undefined
          : `router.pages.${String(route.name)
              .split('/')
              .filter((x) => x)
              .join('-')
              .replace(/\[/g, '')
              .replace(/\]/g, '')}`

      const breadcrumbItems = buildWorkspaceBreadcrumbs(
        workspace.value.id,
        workspace.value.nameDE,
        currentPageTitle,
      )

      setBreadcrumbs(breadcrumbItems)
    }
  },
)

watch(canManageFeeOverview, () => {
  redirectUnauthorizedWorkspacePage()
})

watch(canManageInvites, () => {
  redirectUnauthorizedWorkspacePage()
})
</script>

<template>
  <div class="content-wrap">
    <!-- Initial loading state (no stale data) -->
    <div v-if="showLoading && !workspace" class="flex items-center justify-center py-12">
      <AppSpinner />
    </div>

    <!-- Error state -->
    <AppAlert v-else-if="error && !workspace" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <!-- Workspace content (shows even while reloading with stale data) -->
    <template v-else-if="workspace">
      <div class="mb-2 p-4 pb-2">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
          <UIcon name="i-lucide-building-2" class="text-3xl text-[var(--app-text-primary)]" />
          <h1 class="text-2xl font-bold">
            {{ workspace.nameDE }}
          </h1>
          <!-- Subtle loading indicator during refresh -->
          <UIcon name="i-lucide-loader-circle" v-if="loading" class="animate-spin text-dimmed" />
          <!-- Shares the title row when there is room, wraps below it on narrow screens -->
          <WorkspaceHeadingFilter v-if="showHeadingFilter" :workspace="workspace" class="ml-auto" />
        </div>

        <p v-if="workspace.note" class="text-muted">
          {{ workspace.note }}
        </p>
      </div>
      <AppTabs :value="activeTab">
        <AppTabList>
          <AppTab
            v-for="tab in items"
            :key="tab.label"
            :value="tab.route.name"
            manual
            @activate="handleTabClick(tab.route)"
          >
            <UIcon :name="tab.icon" />
            <span>{{ tab.label }}</span>
          </AppTab>
        </AppTabList>
      </AppTabs>
      <div class="py-4">
        <router-view
          :workspace="workspace"
          @reload-workspace="() => fetchWorkspace(workspace!.id, true)"
        />
      </div>
    </template>
  </div>
</template>
